import { ArtifactCard } from "@artifacts/ArtifactCard";
import { artifactsClient } from "@artifacts/artifactsClient";
import { useArtifactsList } from "@artifacts/artifactsQueries";
import { useSessionStore } from "@auth/sessionStore";
import { CompanionPanel } from "@components/CompanionPanel";
import {
  EmptyNote,
  PanelError,
  SkeletonStack,
} from "@scribenote/brian-design-system";
import { useActiveSubject } from "@workspace/useActiveSubject";

export const ArtifactsPanel = () => {
  const subjectId = useActiveSubject()?.id ?? null;

  return (
    <CompanionPanel
      skeleton={<ArtifactsSkeleton />}
      fallback={(retry) => <PanelError label="artifacts" onRetry={retry} />}
      resetKeys={[subjectId]}
    >
      <ArtifactsList subject={subjectId} />
    </CompanionPanel>
  );
};

const ArtifactsList = ({ subject }: { subject: string | null }) => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const { data: artifacts } = useArtifactsList(sessionKey, subject);

  const download = async (id: string) => {
    try {
      const url = await artifactsClient.getDownloadUrl(id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      // The presigned link couldn't be fetched; the card stays in place.
    }
  };

  if (artifacts.length === 0)
    return (
      <EmptyNote>
        {subject ? "Nothing filed under this one." : "Nothing filed yet."}
      </EmptyNote>
    );

  return artifacts.map((a) => (
    <ArtifactCard
      key={a.id}
      artifact={a}
      onClick={
        a.status === "stored"
          ? () => {
              void download(a.id);
            }
          : undefined
      }
    />
  ));
};

const ArtifactsSkeleton = () => (
  <SkeletonStack count={4} className="gap-3" itemClassName="h-16" />
);
